import { RefObject, createContext, Component } from 'react'
import * as Hammer from 'hammerjs'
import * as debounce from 'lodash/debounce'
import * as difference from 'lodash/difference'
import { doesSupportTouch } from 'helpers/styleHelpers'

type ListenerNode =
  | Node
  | RefObject<Node>
  | ((node: EventTarget | null) => boolean) // true means need to execute callback
  | null

export type OuterEventListener = (e: Event | HammerInput) => void

type Callback = OuterEventListener | RefObject<OuterEventListener | undefined>

type Listener = {
  callback: Callback
  node: ListenerNode
  removed?: boolean
}

export type OuterEventTrackerContextType = {
  // TODO provide options to subscription (capturing, target, etc.)
  addOuterEventListener: (events: string, callback: Callback, node: ListenerNode) => void
  removeOuterEventListener: (events: string, callback: Callback) => void
}

const HAMMER_EVENTS = new Set(['tap'])
// TODO move debounce logic to a consumer side
const DEBOUNCE_EVENTS = new Set(['scroll'])
const DOES_SUPPORT_TOUCH = doesSupportTouch()

export const OuterEventTrackerContext = createContext<OuterEventTrackerContextType>({
  addOuterEventListener: () => null,
  removeOuterEventListener: () => null,
})

class OuterEventTracker extends Component {
  private listeners: Record<string, Listener[]> = {}
  private bodyHammer: HammerManager | null = null
  private subscribedEvents: Set<string> = new Set()

  constructor (props: any, context: any) {
    super(props, context)

    this.notifyAll = this.notifyAll.bind(this)
  }

  render () {
    return (
      <OuterEventTrackerContext.Provider value={ this.getContextValue() }>
        { this.props.children }
      </OuterEventTrackerContext.Provider>
    )
  }

  private getContextValue (): OuterEventTrackerContextType {
    return {
      addOuterEventListener: this.addOuterEventListener,
      removeOuterEventListener: this.removeOuterEventListener,
    }
  }

  private getNotifyHandlerByEvent = (eventName: string) => {
    return DEBOUNCE_EVENTS.has(eventName) ? this.notifyAllDebounced : this.notifyAll
  }

  private ensureSubscribed () {
    Object.keys(this.listeners).forEach((eventName) => {
      if (HAMMER_EVENTS.has(eventName)) {
        if (!this.bodyHammer) {
          this.bodyHammer = new Hammer(document.body, {
            touchAction: 'pan-y pinch-zoom',
            cssProps: {
              ...Hammer.defaults.cssProps,
              // it can cause issues with dragging, holding etc., but now we need only tap
              // more info there:
              // http://hammerjs.github.io/tips/
              // https://github.com/hammerjs/hammer.js/issues/81
              userSelect: 'auto',
              userDrag: 'auto',
            },
          })
        }

        if (!this.subscribedEvents.has(eventName)) {
          this.bodyHammer.on(eventName, this.notifyAll as HammerListener)
          this.subscribedEvents.add(eventName)
        }
      } else {
        if (!this.subscribedEvents.has(eventName)) {
          document.addEventListener(eventName, this.getNotifyHandlerByEvent(eventName), true)
          this.subscribedEvents.add(eventName)
        }
      }
    })
  }

  private ensureUnsubscribed () {
    Array.from(this.subscribedEvents).forEach((eventName) => {
      const listeners = this.getSafeListenersByEvent(eventName)
      if (listeners.length === 0) {
        if (HAMMER_EVENTS.has(eventName)) {
          if (this.bodyHammer) {
            this.bodyHammer.off(eventName, this.notifyAll)
          }
        } else {
          document.removeEventListener(eventName, this.getNotifyHandlerByEvent(eventName), true)
        }

        this.subscribedEvents.delete(eventName)
      }
    })

    // determine if there is any hammer subscription
    const diffEvents = difference(Array.from(HAMMER_EVENTS), Object.keys(this.listeners))
    const destroyHammer = diffEvents.length === HAMMER_EVENTS.size

    if (destroyHammer) {
      this.destroyHammer()
    }
  }

  private getSafeListenersByEvent = (eventName: string) =>
    this.listeners[eventName] || []

  private destroyHammer = () => {
    if (!this.bodyHammer) {
      return
    }

    this.bodyHammer.stop(true)
    this.bodyHammer.destroy()
    this.bodyHammer = null
  }

  private notifyAll (e: HammerInput): void
  private notifyAll (e: Event): void
  private notifyAll (e: Event | HammerInput): void {
    for (const listener of this.getSafeListenersByEvent(e.type)) {
      const {
        node,
        removed,
      } = listener

      if (removed) {
        continue
      }

      let isOuterEvent = false
      if (!node) {
        isOuterEvent = false
      } else if (typeof node === 'function') {
        isOuterEvent = node(e.target)
      } else {
        const guardNode = 'current' in node ? node.current : node
        if (guardNode) {
          isOuterEvent = !guardNode.contains(e.target as Node)
        }
      }

      if (isOuterEvent) {
        const callback: OuterEventListener | null | undefined = 'current' in listener.callback ? listener.callback.current : listener.callback
        if (callback) {
          callback(e)
        }
      }
    }
  }

  private notifyAllDebounced = debounce(this.notifyAll.bind(this), 100)

  private addOuterEventListener: OuterEventTrackerContextType['addOuterEventListener'] = (events: string, callback, node = null) => {
    const splitEvents = events.split(' ')
    splitEvents.forEach((eventName) => {
      // don't subscribe on touch events on a regular device
      // TODO @pmyagkov remove `DOES_SUPPORT_TOUCH` check; it should support all kinds of devices
      if (HAMMER_EVENTS.has(eventName) && !DOES_SUPPORT_TOUCH) {
        return
      }
      this.listeners[eventName] = this.getSafeListenersByEvent(eventName).concat({ callback, node })
    })

    this.ensureSubscribed()
  }

  private removeOuterEventListener: OuterEventTrackerContextType['removeOuterEventListener'] = (events: string, callback) => {
    const splitEvents = events.split(' ')
    splitEvents.forEach((eventName) => {
      const newListeners = [
        ...this.getSafeListenersByEvent(eventName),
      ]

      const index = newListeners.findIndex((cb) => cb.callback === callback)
      if (index === -1) {
        return
      }

      const [removedCallback] = newListeners.splice(index, 1)
      removedCallback.removed = true

      if (newListeners.length) {
        this.listeners[eventName] = newListeners
      } else {
        delete this.listeners[eventName]
      }
    })

    this.ensureUnsubscribed()
  }
}

export default OuterEventTracker
